// ---- Coded wearables ----
// Geometry that the content/parts prim language can't express (open-front
// cloaks, etc.) lives here as TypeScript builders. Items opt in with
// `wear.coded: "<id>"`. Attach runs AFTER the player prim build so pieces
// parent under real rig limbs (torso / head).

import type * as THREE from "three";
import { attachCloak, buildCloakGround } from "./cloak.ts";

export type CodedWearDef = {
  /** Parent meshes onto a built humanoid rig. */
  attach: (root: THREE.Group) => void;
  /** Build a freestanding ground/shop drop into `group` (feet at y≈0). */
  buildGround: (group: THREE.Group) => void;
  /** When true, mergeWear strips hair (hoods). */
  hidesHair?: boolean;
};

export const CODED_WEAR: Record<string, CodedWearDef> = {
  cloak: {
    attach: (root) => attachCloak(root, { hooded: false }),
    buildGround: (g) => buildCloakGround(g, { hooded: false }),
  },
  "hooded-cloak": {
    attach: (root) => attachCloak(root, { hooded: true }),
    buildGround: (g) => buildCloakGround(g, { hooded: true }),
    hidesHair: true,
  },
};

export function codedWearDef(id: string): CodedWearDef | undefined {
  return CODED_WEAR[id];
}

/** Attach every coded wearable in `gear` onto a built player/NPC group. */
export function attachCodedWear(
  root: THREE.Group,
  gear: Partial<Record<string, string | undefined>>,
  resolveWearCoded: (itemType: string) => string | undefined,
): void {
  for (const type of Object.values(gear)) {
    if (!type) continue;
    const id = resolveWearCoded(type);
    if (!id) continue;
    CODED_WEAR[id]?.attach(root);
  }
}

export { attachCloak, buildCloakGround, findLimb, updateCloakHem } from "./cloak.ts";
export {
  wearSeatFromRig,
  yokeFromRig,
  seatingWarns,
  hoodSeatFromRig,
  hoodSeatFromScale,
  hoodSeatingWarns,
  sleeveSeatFromRig,
  sleeveSeatingWarns,
  wearBodyFromRig,
  recordCodedWearSeat,
  AVERAGE_MALE_SEAT,
  AVERAGE_MALE_HOOD,
  AVERAGE_MALE_SLEEVE,
  AVERAGE_MALE_BODY,
  TORSO_SHELL_MIN_MUL,
  TORSO_FRONT_MIN_MUL,
  LEG_SHELL_MIN_MUL,
  FOOT_CUFF_MIN_MUL,
  SHOULDER_PAD_MIN_MUL,
  WRAP_JUDGE_FRACTION,
  BOX_WRAP_JUDGE_FRACTION,
  BACK_WRAP_MIN_Z,
  type WearSeat,
  type HoodSeat,
  type SleeveSeat,
  type WearBodySeat,
  type CodedWearSeatState,
} from "./seat.ts";
